/**
 * The sidebar nav vocabulary — per-team visibility and landing page
 * (TRA-927) are keyed by these `to` route paths. Must match
 * KNOWN_NAV_ROUTES in api/app/services/keycloak_admin.py — add new nav
 * entries to both lists. Route paths, not labels: labels get renamed.
 *
 * Settings (and the sections inside it, Prompts and Billing included since
 * TRA-1185) is not a nav entry: it is reached from the team menu and has
 * its own registry in components/settings/sections.ts. A section that
 * configures one of these pages names it in its `navRoute` and hides with
 * it (canAccessSection there), so hiding Review hides its Jobs sections.
 */

import {
  Activity,
  Boxes,
  Filter,
  Flag,
  FlaskConical,
  Route,
  House,
} from 'lucide-react'

export interface NavEntry {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  /**
   * Hidden unless the team's loaded config says otherwise. The team config
   * is the source of truth once it arrives; this only decides the answer
   * BEFORE it does, so a route carrying data customers must not see stays
   * closed during the /me window instead of flashing open.
   */
  defaultHidden?: boolean
}

export const NAV_ENTRIES: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: House },
  { to: '/evals', label: 'Evals', icon: FlaskConical },
  { to: '/models', label: 'Models', icon: Boxes },
  { to: '/review', label: 'Review', icon: Flag },
  { to: '/trajectories', label: 'Trajectories', icon: Route },
  // Hidden by default for every team except the demo team —
  // DEMO_SUITE_HIDDEN_NAV in api/app/tools/backfill_restricted_nav.py
  // writes that default, and staff flip it per team on the Employees page.
  // /refinery shows the tasks the gate proved for the team (TRA-1477):
  // unhide it once the gate proves a task for it. /training shows
  // the team's real training runs (pages/TrainingPage.tsx reads
  // /api/training-runs): unhide it once the team has a run to show.
  { to: '/refinery', label: 'Data refinery', icon: Filter, defaultHidden: true },
  { to: '/training', label: 'Training', icon: Activity, defaultHidden: true },
  // Datasets stays routable without a nav entry (per Jackson); so does
  // /conversations, which is reached from the traffic bands.
]

/**
 * Whether a path is a nav route this app serves.
 *
 * Used to validate `landing_page`, which is a free-form string attribute on
 * a Keycloak group rather than anything this app controls. This is NOT a
 * compatibility shim for deleted paths (/traces, and /prompts since it
 * moved into Settings) — the backfill scrubs those out of storage once
 * (api/app/tools/backfill_restricted_nav.py). Turning an untrusted redirect
 * target into a page the router can render is a permanent property, the
 * same one the two guards below it enforce.
 */
export function isKnownRoute(to: string): boolean {
  return NAV_ENTRIES.some((entry) => entry.to === to)
}

/**
 * Whether a nav route is visible to the active team.
 *
 * Until `navConfigLoaded`, `hiddenNavItems` is the JWT-fallback empty list,
 * which reads as "hide nothing" — so a defaultHidden route must be treated
 * as hidden until the real config arrives. Without that, every customer
 * gets a flash of the demo suite on each cold load, and its URLs answer
 * during the window.
 */
export function isNavRouteVisible(opts: {
  to: string
  hiddenNavItems: string[]
  navConfigLoaded: boolean
}): boolean {
  if (opts.hiddenNavItems.includes(opts.to)) {
    return false
  }
  const entry = NAV_ENTRIES.find((e) => e.to === opts.to)
  return opts.navConfigLoaded || entry?.defaultHidden !== true
}

/**
 * Where "/" should send the user, or null to render the Dashboard.
 *
 * - On app ENTRY (`isEntry`), the team's landing page wins over Dashboard.
 * - A landing page whose route the app no longer serves (a deleted page,
 *   still named by the stored attribute) would redirect "/" straight to
 *   NotFoundPage, so it degrades to the Dashboard first and logs the bad
 *   config. A team still landing on /prompts after TRA-1185 takes this
 *   path until the backfill rewrites its attribute.
 * - When "/" itself is hidden, every visit to "/" redirects to the landing
 *   page — the sidebar has no Dashboard entry, so rendering it would strand
 *   the user on a hidden page.
 * - A landing page the team has hidden (NavRoute in App.tsx bounces it
 *   back to "/") degrades the same way; the API rejects that combination
 *   at write time, but pre-existing config must not loop.
 */
export function resolveHomeRedirect(opts: {
  hiddenNavItems: string[]
  landingPage: string
  isEntry: boolean
}): string | null {
  let landing = opts.landingPage
  if (!isKnownRoute(landing)) {
    console.error(
      `Landing page ${landing} is not a route this app serves — falling ` +
        'back to the Dashboard. Fix the team\'s landing_page attribute.'
    )
    landing = '/'
  }
  if (landing !== '/' && opts.hiddenNavItems.includes(landing)) {
    console.error(
      `Landing page ${landing} is hidden for this team — falling back to ` +
        'the Dashboard. Fix the team\'s landing_page attribute.'
    )
    landing = '/'
  }
  if (landing !== '/' && (opts.hiddenNavItems.includes('/') || opts.isEntry)) {
    return landing
  }
  return null
}
